// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import type { UserDetail } from "@/api/hooks/use-users";
import { DateTimeValue } from "@/components/shared/datetime-value";
import { KeyChip } from "@/components/shared/key-chip";
import { StoreLink } from "@/components/shared/store-link";
import { UserLink } from "@/components/shared/user-link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { RealmBadge } from "@/features/iam/realm-badge";

export function UserInfoTab({ detail }: { detail: UserDetail }) {
  const { t } = useTranslation("back-office");
  const { user, groups, defaultStore, availableStores, permissions, flags, supervisor } = detail;
  // Directly-assigned permissions first, then the group-sourced ones
  const orderedPermissions = [
    ...permissions.filter((source) => source.groupName === null),
    ...permissions.filter((source) => source.groupName !== null),
  ];

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
        {user.fullName && (
          <>
            <span className="text-muted-foreground">{t("users.detail.fullName")}</span>
            <span>{user.fullName}</span>
          </>
        )}

        <span className="text-muted-foreground">{t("users.list.columns.email")}</span>
        <span>{user.email}</span>

        {user.userName && (
          <>
            <span className="text-muted-foreground">{t("users.list.columns.userName")}</span>
            <span>{user.userName}</span>
          </>
        )}

        <span className="text-muted-foreground">{t("users.list.columns.publicId")}</span>
        <span>
          <KeyChip label={user.publicId} />
        </span>

        <span className="text-muted-foreground">{t("users.list.columns.realm")}</span>
        <span>
          <RealmBadge realm={user.realm} />
        </span>

        <span className="text-muted-foreground">{t("users.list.columns.mfa")}</span>
        <Switch checked={user.hasMfa} tabIndex={-1} aria-readonly className="pointer-events-none" />

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("users.list.columns.defaultStore")}</span>
        <span>
          {defaultStore ? (
            <StoreLink storeName={defaultStore.storeName} legibleName={defaultStore.legibleName} />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>

        <span className="text-muted-foreground">{t("users.detail.availableStores")}</span>
        <span>
          {availableStores.length > 0 ? (
            <span className="flex flex-wrap gap-x-3 gap-y-1.5">
              {availableStores.map((store) => (
                <StoreLink key={store.storeId} storeName={store.storeName} legibleName={store.legibleName} />
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>

        <span className="text-muted-foreground">{t("users.list.columns.lastLoginAt")}</span>
        <DateTimeValue value={user.lastLoginAt} />

        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("users.detail.groupsTitle")}</span>
        <span>
          {groups.length > 0 ? (
            <span className="flex flex-wrap gap-1.5">
              {groups.map((group) => (
                <Badge key={group} variant="secondary">
                  {group}
                </Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>

        {supervisor && (
          <>
            <span className="text-muted-foreground">{t("users.form.supervisor")}</span>
            <span>
              <UserLink publicId={supervisor.publicId} legibleName={supervisor.fullName || supervisor.email} />
            </span>
          </>
        )}

        {flags.length > 0 && (
          <>
            <span className="text-muted-foreground">{t("users.form.flags")}</span>
            <span className="flex flex-wrap gap-1.5">
              {flags.map((flag) => (
                <Badge key={flag} variant="outline" className="font-mono font-normal">
                  {flag}
                </Badge>
              ))}
            </span>
          </>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-medium">{t("users.detail.permissionsTitle")}</h3>
        {permissions.length > 0 ? (
          <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
            {orderedPermissions.map((source, index) => (
              <Fragment key={source.groupName ?? "__direct__"}>
                {index > 0 && source.groupName !== null && orderedPermissions[index - 1]?.groupName === null && (
                  <Separator className="col-span-2" />
                )}
                <span className="text-muted-foreground">{source.groupName ?? t("users.detail.directlyAssigned")}</span>
                <span className="flex flex-wrap gap-1.5">
                  {[...new Set(source.permissions)].map((permission) => (
                    <Badge key={permission} variant="outline" className="font-mono font-normal">
                      {permission}
                    </Badge>
                  ))}
                </span>
              </Fragment>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">—</p>
        )}
      </div>

      <div className="-mt-3 grid grid-cols-[160px_1fr] gap-y-3 text-sm">
        <Separator className="col-span-2" />

        <span className="text-muted-foreground">{t("common.createdAt")}</span>
        <DateTimeValue value={user.createdAt} />

        <span className="text-muted-foreground">{t("common.updatedAt")}</span>
        <DateTimeValue value={user.updatedAt} />
      </div>
    </div>
  );
}
